import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';

import { BrandAffinityLevel, ensureBrandAffinityLevel } from 'modules/domains/brandAffinity';
import { MessageMedium } from 'constants/messageMediums';
import { ensureEnum } from 'utils/tsHelpers';
import { Requested } from 'modules/ai/types';
import { safeGet } from 'utils/fp/safeGet';

import XAIDataMock from '../__mocks__/data/XAIData.mock.json';
import { BrandAffinityXAI, FilteredTopCampaign, XAIFilteredCampaign } from '../types';
import { CampaignData } from '../../types';

export const caseInsensitiveComparator =
  <EnumTypeValue>(value: unknown) =>
  (enumVal: EnumTypeValue) =>
    (value as string).toLowerCase() === (enumVal as string).toLowerCase();

export const ensureMessageChannel = (input: unknown) =>
  ensureEnum(input, MessageMedium, MessageMedium.Email, caseInsensitiveComparator);

export const defaultXAIData = {
  computedDate: '',
  userId: '',
  label: BrandAffinityLevel.NEUTRAL,
  history: [],
  topCampaigns: [],
  userStats: [],
};

export const parseXAIData = (XAIData: Requested<BrandAffinityXAI>) => {
  if (isEmpty(XAIData)) {
    return defaultXAIData;
  }

  const responseData = XAIData as BrandAffinityXAI;
  return merge({}, responseData, {
    label: ensureBrandAffinityLevel(responseData.label),
    history:
      responseData.history?.map((history) => ({
        ...history,
        label: ensureBrandAffinityLevel(history.label as string),
      })) || [],
    topCampaigns:
      responseData.topCampaigns?.map((campaign) => ({
        ...campaign,
        channel: ensureMessageChannel(campaign.channel),
      })) || [],
  });
};

export const getCampaignIds = (XAIData: Requested<BrandAffinityXAI>) => {
  if (isEmpty(XAIData)) {
    return [];
  }
  return XAIData?.topCampaigns?.map((campaign) => campaign.campaignId) || [];
};

export const getTemplateIds = (campaignData: Requested<Array<CampaignData>>) => {
  if (isEmpty(campaignData)) {
    return [];
  }
  return campaignData?.map((campaign) => campaign.templateId) || [];
};

export const createCombinedCampaignData = (
  XAIData: Requested<BrandAffinityXAI>,
  campaignData: Requested<Array<XAIFilteredCampaign>>,
): Array<FilteredTopCampaign> => {
  if (isEmpty(XAIData) || isEmpty(campaignData)) {
    return [];
  }

  // There is a 1:1 ratio of campaign data requested to top campaigns count
  return (XAIData as BrandAffinityXAI)?.topCampaigns.map((topCampaign) => {
    return {
      ...topCampaign,
      ...safeGet<Array<XAIFilteredCampaign>, XAIFilteredCampaign>(
        (campaigns) =>
          campaigns.find(
            (campaign) => Number(campaign.id) === Number(topCampaign.campaignId),
          ) as XAIFilteredCampaign,
        {} as XAIFilteredCampaign,
      )(campaignData as Array<XAIFilteredCampaign>),
    };
  });
};

/** Temporary mock functions */
export const createDelayedRequest =
  <TResponse>(delayFor: number, request: Promise<TResponse>) =>
  () =>
    Promise.all([new Promise<void>((res) => setTimeout(() => res(), delayFor)), request]).then(
      (data) => data[1],
    );

export const createMockXAIRequest = (): Promise<BrandAffinityXAI> =>
  Promise.resolve(XAIDataMock.explainBrandAffinityForUserProfile as any);

export const createMockCampaignDetails = () => ({
  data: {
    campaigns: {
      results: XAIDataMock.CampaignsDetails,
    },
  },
});

export const createMockTemplatePreviews = () => Promise.resolve(XAIDataMock.TemplatePreviewDetails);
